import { RefObject, useEffect } from "react"

interface UseOutsideClickOptions {
    enabled?: boolean
}

/**
 * useOutsideClick
 *
 * Calls the given action when the user clicks outside of the
 * element that the given ref is pointing to.
 *
 * @param action The action to call when the user clicks outside
 * @param ref The ref to the HTMLElement that should be considered the "outside"
 * @param options Options for the hook
 * @param options.enabled If this is false, the hook will not register the event listener
 */
export function useOutsideClick<T extends HTMLElement>(
    action: () => void,
    ref: RefObject<T>,
    { enabled = true }: UseOutsideClickOptions = {}
) {
    useEffect(() => {
        if (!enabled) return

        const handleClick = (event: MouseEvent) => {
            const target = event.target
            if (target instanceof HTMLElement && !ref.current?.contains(target)) {
                action()
            }
        }

        document.addEventListener("click", handleClick)
        return () => document.removeEventListener("click", handleClick)
    }, [action, enabled])
}
